import React, { useState, useMemo, useCallback } from 'react';
import {
    Rocket, Sun, Zap, LayoutGrid, Calendar, Brain, Plus, Search,
    ChevronRight, ChevronDown, Target, Clock, Flame, CheckCircle2,
    AlertTriangle, Lightbulb, Trophy, Bell, Shield, X, ArrowRight,
    MoreHorizontal, Tag, User, Sparkles, TrendingUp, BarChart3,
    PanelLeftClose, PanelLeftOpen, Filter,
} from 'lucide-react';
import {
    Project, ProjectTask, ReplicaInsight, CCTab, TaskStatus, TaskPriority,
    KANBAN_COLUMNS, PRIORITY_CONFIG, PROJECT_COLORS,
} from './types';
import { MOCK_PROJECTS, MOCK_TASKS, MOCK_INSIGHTS } from './mockData';
import WorkModePage from '../WorkModePage';
import WorkflowsPage from '../WorkflowsPage';
import DailyRoutinePage from '../DailyRoutinePage';

// ============================================================================
// Helpers
// ============================================================================
const getColor = (c: string) => PROJECT_COLORS[c] || PROJECT_COLORS.blue;
const daysUntil = (d: string) => Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);
const fmtDate = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const pct = (tasks: ProjectTask[]) => {
    if (!tasks.length) return 0;
    return Math.round((tasks.filter(t => t.status === 'done').length / tasks.length) * 100);
};

// ============================================================================
// Sub-components
// ============================================================================

const AvatarCircle: React.FC<{ a: { name: string; avatar: string } | null; size?: number }> = ({ a, size = 24 }) => (
    <div style={{ width: size, height: size, borderRadius: '50%', background: a ? 'linear-gradient(135deg, #8b5cf6, #3b82f6)' : 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.42, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
        {a?.avatar || '?'}
    </div>
);

const PriorityDot: React.FC<{ p: TaskPriority }> = ({ p }) => <div className={`priority-dot ${PRIORITY_CONFIG[p].cssClass}`} title={PRIORITY_CONFIG[p].label} />;

const DueBadge: React.FC<{ d: string | null }> = ({ d }) => {
    if (!d) return null;
    const days = daysUntil(d);
    const color = days < 0 ? '#FF453A' : days <= 2 ? '#FF9F0A' : 'var(--text-tertiary)';
    return <span style={{ fontSize: 10, color, fontWeight: 600 }}>{days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Today' : `${days}d`}</span>;
};

const SubtaskBar: React.FC<{ subs: { completed: boolean }[] }> = ({ subs }) => {
    if (!subs.length) return null;
    const done = subs.filter(s => s.completed).length;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-tertiary)' }}>
            <div style={{ width: 40, height: 3, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
                <div style={{ width: `${(done / subs.length) * 100}%`, height: '100%', borderRadius: 2, background: 'var(--accent)', transition: 'width 0.3s' }} />
            </div>
            {done}/{subs.length}
        </div>
    );
};

// ============================================================================
// Main Component
// ============================================================================

interface CommandCenterPageProps {
    onNavigate: (view: string) => void;
}

export default function CommandCenterPage({ onNavigate }: CommandCenterPageProps) {
    // ── State ────────────────────────────────────────────────
    const [projects, setProjects] = useState<Project[]>(MOCK_PROJECTS);
    const [tasks, setTasks] = useState<ProjectTask[]>(MOCK_TASKS);
    const [insights] = useState<ReplicaInsight[]>(MOCK_INSIGHTS);
    const [activeProjectId, setActiveProjectId] = useState<string>('p1');
    const [activeTab, setActiveTab] = useState<CCTab>('today');
    const [showSidebar, setShowSidebar] = useState(true);
    const [selectedTask, setSelectedTask] = useState<ProjectTask | null>(null);
    const [showCreateProject, setShowCreateProject] = useState(false);
    const [dragTaskId, setDragTaskId] = useState<string | null>(null);
    const [dragOverCol, setDragOverCol] = useState<TaskStatus | null>(null);
    const [quickAddCol, setQuickAddCol] = useState<TaskStatus | null>(null);
    const [quickAddTitle, setQuickAddTitle] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [newProjectName, setNewProjectName] = useState('');
    const [newProjectType, setNewProjectType] = useState<'personal' | 'professional'>('personal');

    // ── Derived ──────────────────────────────────────────────
    const activeProject = projects.find(p => p.id === activeProjectId)!;
    const projectTasks = useMemo(() => tasks.filter(t => t.projectId === activeProjectId), [tasks, activeProjectId]);
    const projectInsights = useMemo(() => insights.filter(i => i.projectId === activeProjectId), [insights, activeProjectId]);
    const allProjectStats = useMemo(() => projects.map(p => {
        const pt = tasks.filter(t => t.projectId === p.id);
        return { ...p, taskCount: pt.length, doneCount: pt.filter(t => t.status === 'done').length, pct: pct(pt) };
    }), [projects, tasks]);
    const completion = pct(projectTasks);

    // ── Actions ──────────────────────────────────────────────
    const updateTaskStatus = useCallback((taskId: string, newStatus: TaskStatus) => {
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus, completedAt: newStatus === 'done' ? new Date().toISOString() : null } : t));
    }, []);

    const addTask = useCallback((status: TaskStatus, title: string) => {
        const newTask: ProjectTask = {
            id: `t${Date.now()}`, projectId: activeProjectId, title, description: '', status, priority: 'medium',
            assignee: null, dueDate: null, tags: [], subtasks: [], milestoneId: null,
            governanceRequired: false, estimatedHours: null, completedAt: null, createdAt: new Date().toISOString(),
        };
        setTasks(prev => [...prev, newTask]);
        setQuickAddCol(null);
        setQuickAddTitle('');
    }, [activeProjectId]);

    const createProject = useCallback(() => {
        if (!newProjectName.trim()) return;
        const colors = Object.keys(PROJECT_COLORS);
        const icons = ['🎯', '💼', '🎓', '🏠', '🎨', '📱', '🌍', '🔬'];
        const np: Project = {
            id: `p${Date.now()}`, name: newProjectName.trim(), description: '', type: newProjectType,
            category: 'custom', status: 'active', color: colors[Math.floor(Math.random() * colors.length)],
            icon: icons[Math.floor(Math.random() * icons.length)], riskLevel: 'low',
            startDate: new Date().toISOString().split('T')[0], targetDate: '', milestones: [], tags: [],
            createdAt: new Date().toISOString(),
        };
        setProjects(prev => [...prev, np]);
        setActiveProjectId(np.id);
        setShowCreateProject(false);
        setNewProjectName('');
    }, [newProjectName, newProjectType]);

    const updateTaskField = useCallback((taskId: string, field: string, value: any) => {
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, [field]: value } : t));
        if (selectedTask?.id === taskId) setSelectedTask(prev => prev ? { ...prev, [field]: value } : null);
    }, [selectedTask]);

    // ── Drag & Drop ──────────────────────────────────────────
    const handleDragStart = (taskId: string) => setDragTaskId(taskId);
    const handleDragOver = (e: React.DragEvent, col: TaskStatus) => { e.preventDefault(); setDragOverCol(col); };
    const handleDragLeave = () => setDragOverCol(null);
    const handleDrop = (col: TaskStatus) => {
        if (dragTaskId) updateTaskStatus(dragTaskId, col);
        setDragTaskId(null); setDragOverCol(null);
    };

    // ── Tabs config ──────────────────────────────────────────
    const TABS: { key: CCTab; label: string; icon: React.ElementType }[] = [
        { key: 'today', label: 'Today', icon: Sun },
        { key: 'operations', label: 'Operations', icon: Zap },
        { key: 'board', label: 'Board', icon: LayoutGrid },
        { key: 'timeline', label: 'Timeline', icon: Calendar },
        { key: 'insights', label: 'Insights', icon: Brain },
    ];

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // RENDER
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    return (
        <div className="animate-fade-in" style={{ display: 'flex', minHeight: 'calc(100vh - 100px)', gap: 0 }}>
            {/* ═══ PROJECTS SIDEBAR ═══ */}
            {showSidebar && (
                <div className="cc-projects-sidebar">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Projects</span>
                        <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={() => setShowCreateProject(true)} style={{ padding: 4, borderRadius: 6, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', display: 'flex' }}><Plus size={12} /></button>
                            <button onClick={() => setShowSidebar(false)} style={{ padding: 4, borderRadius: 6, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', display: 'flex' }}><PanelLeftClose size={14} /></button>
                        </div>
                    </div>
                    {/* Search */}
                    <div style={{ position: 'relative', marginBottom: 8 }}>
                        <Search size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
                        <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search projects..." style={{ width: '100%', padding: '6px 8px 6px 26px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: 12, outline: 'none' }} />
                    </div>
                    {/* Project list */}
                    {allProjectStats.filter(p => !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase())).map(p => {
                        const c = getColor(p.color);
                        return (
                            <button key={p.id} onClick={() => setActiveProjectId(p.id)} className={`cc-project-item ${activeProjectId === p.id ? 'active' : ''}`} style={{ textAlign: 'left', width: '100%', background: activeProjectId === p.id ? c.bg : undefined }}>
                                <span style={{ fontSize: 18 }}>{p.icon}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: p.type === 'personal' ? 'rgba(59,130,246,0.1)' : 'rgba(249,115,22,0.1)', color: p.type === 'personal' ? '#3b82f6' : '#f97316', textTransform: 'uppercase' }}>{p.type === 'personal' ? 'PER' : 'PRO'}</span>
                                        <div style={{ flex: 1, height: 3, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
                                            <div style={{ width: `${p.pct}%`, height: '100%', borderRadius: 2, background: c.gradient, transition: 'width 0.5s' }} />
                                        </div>
                                        <span style={{ fontSize: 10, color: 'var(--text-tertiary)', fontWeight: 600 }}>{p.pct}%</span>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* ═══ MAIN CONTENT ═══ */}
            <div style={{ flex: 1, padding: '0 24px 24px', minWidth: 0, overflow: 'hidden' }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    {!showSidebar && (
                        <button onClick={() => setShowSidebar(true)} style={{ padding: 6, borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', display: 'flex' }}><PanelLeftOpen size={16} /></button>
                    )}
                    <div style={{ width: 40, height: 40, borderRadius: 12, background: getColor(activeProject.color).gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{activeProject.icon}</div>
                    <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{activeProject.name}</h1>
                            <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: activeProject.riskLevel === 'high' || activeProject.riskLevel === 'critical' ? 'rgba(255,69,58,0.1)' : activeProject.riskLevel === 'medium' ? 'rgba(255,159,10,0.1)' : 'rgba(48,209,88,0.1)', color: activeProject.riskLevel === 'high' || activeProject.riskLevel === 'critical' ? '#FF453A' : activeProject.riskLevel === 'medium' ? '#FF9F0A' : '#30D158', textTransform: 'uppercase' }}>{activeProject.riskLevel} risk</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 2, fontSize: 12, color: 'var(--text-tertiary)' }}>
                            <span>{projectTasks.length} tasks</span>
                            <span>{completion}% complete</span>
                            {activeProject.targetDate && <span>{daysUntil(activeProject.targetDate)}d remaining</span>}
                        </div>
                    </div>
                    {/* Progress ring */}
                    <div style={{ position: 'relative', width: 48, height: 48 }}>
                        <svg width="48" height="48" viewBox="0 0 48 48"><circle cx="24" cy="24" r="20" fill="none" stroke="var(--border)" strokeWidth="3" /><circle cx="24" cy="24" r="20" fill="none" stroke={getColor(activeProject.color).text} strokeWidth="3" strokeLinecap="round" strokeDasharray={`${(completion / 100) * 125.6} 125.6`} transform="rotate(-90 24 24)" style={{ transition: 'stroke-dasharray 0.5s ease' }} /></svg>
                        <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{completion}%</span>
                    </div>
                </div>

                {/* Tab Bar */}
                <div className="cc-tab-bar" style={{ marginBottom: 20 }}>
                    {TABS.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`cc-tab ${activeTab === tab.key ? 'active' : ''}`}>
                                <Icon size={14} />{tab.label}
                                {tab.key === 'insights' && projectInsights.filter(i => i.severity === 'critical').length > 0 && (
                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#FF453A', flexShrink: 0 }} />
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* ━━━ TODAY VIEW ━━━ */}
                {activeTab === 'today' && <TodayView tasks={projectTasks} project={activeProject} insights={projectInsights} onSelectTask={setSelectedTask} />}

                {/* ━━━ OPERATIONS VIEW ━━━ */}
                {activeTab === 'operations' && <OperationsView onNavigate={onNavigate} />}

                {/* ━━━ BOARD VIEW ━━━ */}
                {activeTab === 'board' && (
                    <BoardView tasks={projectTasks} dragTaskId={dragTaskId} dragOverCol={dragOverCol} quickAddCol={quickAddCol} quickAddTitle={quickAddTitle}
                        onDragStart={handleDragStart} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
                        onSelectTask={setSelectedTask} onQuickAddCol={setQuickAddCol} onQuickAddTitle={setQuickAddTitle} onAddTask={addTask} />
                )}

                {/* ━━━ TIMELINE VIEW ━━━ */}
                {activeTab === 'timeline' && <TimelineView tasks={projectTasks} project={activeProject} onSelectTask={setSelectedTask} />}

                {/* ━━━ INSIGHTS VIEW ━━━ */}
                {activeTab === 'insights' && <InsightsView insights={projectInsights} tasks={projectTasks} project={activeProject} onSelectTask={(id) => { const t = tasks.find(t => t.id === id); if (t) setSelectedTask(t); }} />}
            </div>

            {/* ═══ TASK DETAIL DRAWER ═══ */}
            {selectedTask && <TaskDrawer task={selectedTask} onClose={() => setSelectedTask(null)} onUpdate={updateTaskField} />}

            {/* ═══ CREATE PROJECT MODAL ═══ */}
            {showCreateProject && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={e => { if (e.target === e.currentTarget) setShowCreateProject(false); }}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }} />
                    <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 460, padding: 28, borderRadius: 20, background: 'var(--bg-primary)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-lg)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>New Project</h3>
                            <button onClick={() => setShowCreateProject(false)} style={{ padding: 4, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer' }}><X size={18} /></button>
                        </div>
                        <input value={newProjectName} onChange={e => setNewProjectName(e.target.value)} placeholder="Project name..." autoFocus style={{ width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', marginBottom: 12 }} onKeyDown={e => e.key === 'Enter' && createProject()} />
                        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                            {(['personal', 'professional'] as const).map(t => (
                                <button key={t} onClick={() => setNewProjectType(t)} style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: `1px solid ${newProjectType === t ? 'var(--accent)' : 'var(--border)'}`, background: newProjectType === t ? 'rgba(10,132,255,0.08)' : 'var(--bg-tertiary)', color: newProjectType === t ? 'var(--accent)' : 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                    {t === 'personal' ? '👤 Personal' : '💼 Professional'}
                                </button>
                            ))}
                        </div>
                        <button onClick={createProject} disabled={!newProjectName.trim()} style={{ width: '100%', padding: '12px', borderRadius: 12, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: newProjectName.trim() ? 1 : 0.4 }}>Create Project</button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ============================================================================
// TODAY VIEW
// ============================================================================
const TodayView: React.FC<{ tasks: ProjectTask[]; project: Project; insights: ReplicaInsight[]; onSelectTask: (t: ProjectTask) => void }> = ({ tasks, project, insights, onSelectTask }) => {
    const urgentTasks = tasks.filter(t => t.status !== 'done' && (t.priority === 'urgent' || t.priority === 'high' || (t.dueDate && daysUntil(t.dueDate) <= 2))).slice(0, 3);
    const recentDone = tasks.filter(t => t.status === 'done').slice(0, 3);
    const criticalInsights = insights.filter(i => i.severity === 'critical' || i.severity === 'warning').slice(0, 3);
    const stats = { total: tasks.length, done: tasks.filter(t => t.status === 'done').length, inProgress: tasks.filter(t => t.status === 'in_progress').length, overdue: tasks.filter(t => t.dueDate && daysUntil(t.dueDate) < 0 && t.status !== 'done').length };

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Stats row */}
            <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                {[
                    { label: 'Total Tasks', value: stats.total, icon: <BarChart3 size={14} />, color: '#3b82f6' },
                    { label: 'Completed', value: stats.done, icon: <CheckCircle2 size={14} />, color: '#10b981' },
                    { label: 'In Progress', value: stats.inProgress, icon: <TrendingUp size={14} />, color: '#f59e0b' },
                    { label: 'Overdue', value: stats.overdue, icon: <AlertTriangle size={14} />, color: stats.overdue > 0 ? '#FF453A' : '#8E8E93' },
                ].map(s => (
                    <div key={s.label} className="glass" style={{ padding: '12px 14px', textAlign: 'center' }}>
                        <div style={{ color: s.color, marginBottom: 4, display: 'flex', justifyContent: 'center' }}>{s.icon}</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>{s.value}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* What Matters Now */}
            <div className="glass" style={{ padding: 18 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}><Target size={14} className="text-hig-accent" /> What Matters Now</h3>
                {urgentTasks.length === 0 ? (
                    <p style={{ fontSize: 13, color: 'var(--text-tertiary)', textAlign: 'center', padding: 20 }}>🎉 All clear! Nothing urgent.</p>
                ) : urgentTasks.map(t => (
                    <button key={t.id} onClick={() => onSelectTask(t)} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', marginBottom: 4 }} className="card-interactive">
                        <PriorityDot p={t.priority} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.title}</div>
                            <DueBadge d={t.dueDate} />
                        </div>
                        <AvatarCircle a={t.assignee} size={20} />
                    </button>
                ))}
            </div>

            {/* Replica Insights */}
            <div className="glass" style={{ padding: 18 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}><Sparkles size={14} style={{ color: '#8b5cf6' }} /> Replica Insights</h3>
                {criticalInsights.length === 0 ? (
                    <p style={{ fontSize: 13, color: 'var(--text-tertiary)', textAlign: 'center', padding: 20 }}>All good — no issues detected.</p>
                ) : criticalInsights.map(i => (
                    <div key={i.id} className={`insight-card severity-${i.severity}`} style={{ marginBottom: 8, padding: 12 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: i.severity === 'critical' ? 'rgba(255,69,58,0.1)' : 'rgba(255,159,10,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {i.severity === 'critical' ? <AlertTriangle size={14} color="#FF453A" /> : <Bell size={14} color="#FF9F0A" />}
                        </div>
                        <div style={{ flex: 1 }}>
                            <p style={{ fontSize: 12, color: 'var(--text-primary)', margin: 0, lineHeight: 1.4 }}>{i.message}</p>
                            {i.actionLabel && <button style={{ marginTop: 6, fontSize: 11, fontWeight: 600, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>{i.actionLabel} →</button>}
                        </div>
                    </div>
                ))}
            </div>

            {/* Recent completions */}
            <div className="glass" style={{ padding: 18, gridColumn: '1 / -1' }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}><Trophy size={14} style={{ color: '#10b981' }} /> Recently Completed</h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {recentDone.length === 0 ? <p style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>No completed tasks yet.</p> : recentDone.map(t => (
                        <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 10, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
                            <CheckCircle2 size={14} color="#10b981" />
                            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t.title}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// OPERATIONS VIEW
// ============================================================================
const OperationsView: React.FC<{ onNavigate: (v: string) => void }> = ({ onNavigate }) => {
    const [expandedPanel, setExpandedPanel] = useState<string | null>('work-mode');
    const toggle = (panel: string) => setExpandedPanel(prev => prev === panel ? null : panel);

    const panels = [
        { key: 'work-mode', label: 'Governed Execution', sublabel: 'Natural-language delegation with task planning, risk analysis & verification', icon: '🎯', color: '#8b5cf6', component: <WorkModePage onNavigate={onNavigate} /> },
        { key: 'workflows', label: 'Active Mandates', sublabel: 'Automated workflows, templates & scheduled missions', icon: '⚡', color: '#3b82f6', component: <WorkflowsPage onNavigate={onNavigate} /> },
        { key: 'routines', label: 'Daily Routines', sublabel: 'Streak tracking, time-of-day rituals & Replica-proposed habits', icon: '☀️', color: '#f59e0b', component: <DailyRoutinePage onNavigate={onNavigate} /> },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Quick summary strip */}
            <div className="glass" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <Shield size={16} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                    All operations below are governed by the <strong style={{ color: 'var(--text-primary)' }}>Decision Guardian™</strong>. Your Replica will pause for approval on high-risk actions.
                </p>
            </div>

            {/* Accordion panels */}
            {panels.map(panel => {
                const isOpen = expandedPanel === panel.key;
                return (
                    <div key={panel.key} className="glass" style={{ overflow: 'hidden', transition: 'all 0.3s ease' }}>
                        {/* Panel Header */}
                        <button
                            onClick={() => toggle(panel.key)}
                            style={{
                                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                                padding: '16px 18px', border: 'none', background: 'transparent',
                                cursor: 'pointer', textAlign: 'left',
                            }}
                        >
                            <div style={{
                                width: 38, height: 38, borderRadius: 10,
                                background: `${panel.color}15`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 18, flexShrink: 0,
                            }}>
                                {panel.icon}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{panel.label}</div>
                                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 1 }}>{panel.sublabel}</div>
                            </div>
                            <div style={{
                                width: 28, height: 28, borderRadius: 8,
                                background: isOpen ? `${panel.color}15` : 'var(--bg-tertiary)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.2s', flexShrink: 0,
                            }}>
                                <ChevronDown size={14} style={{
                                    color: isOpen ? panel.color : 'var(--text-tertiary)',
                                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.25s ease',
                                }} />
                            </div>
                        </button>
                        {/* Panel Body — embed real component */}
                        {isOpen && (
                            <div style={{
                                borderTop: '1px solid var(--divider)',
                                padding: '16px',
                                maxHeight: 'calc(100vh - 320px)',
                                overflowY: 'auto',
                                animation: 'fadeIn 0.2s ease',
                            }}>
                                {panel.component}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// ============================================================================
// BOARD VIEW (KANBAN)
// ============================================================================
interface BoardViewProps {
    tasks: ProjectTask[]; dragTaskId: string | null; dragOverCol: TaskStatus | null;
    quickAddCol: TaskStatus | null; quickAddTitle: string;
    onDragStart: (id: string) => void; onDragOver: (e: React.DragEvent, col: TaskStatus) => void;
    onDragLeave: () => void; onDrop: (col: TaskStatus) => void;
    onSelectTask: (t: ProjectTask) => void; onQuickAddCol: (c: TaskStatus | null) => void;
    onQuickAddTitle: (t: string) => void; onAddTask: (s: TaskStatus, t: string) => void;
}

const BoardView: React.FC<BoardViewProps> = ({ tasks, dragTaskId, dragOverCol, quickAddCol, quickAddTitle, onDragStart, onDragOver, onDragLeave, onDrop, onSelectTask, onQuickAddCol, onQuickAddTitle, onAddTask }) => (
    <div className="kanban-container">
        {KANBAN_COLUMNS.map(col => {
            const colTasks = tasks.filter(t => t.status === col.key);
            return (
                <div key={col.key} className={`kanban-column ${dragOverCol === col.key ? 'drag-over' : ''}`}
                    onDragOver={e => onDragOver(e, col.key)} onDragLeave={onDragLeave} onDrop={() => onDrop(col.key)}>
                    <div className="kanban-column-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: col.color }} />
                            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{col.label}</span>
                            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', background: 'var(--bg-tertiary)', padding: '1px 6px', borderRadius: 6 }}>{colTasks.length}</span>
                        </div>
                        <button onClick={() => onQuickAddCol(col.key)} style={{ padding: 2, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer' }}><Plus size={14} /></button>
                    </div>
                    <div className="kanban-column-body">
                        {colTasks.map(t => (
                            <div key={t.id} className={`task-card ${dragTaskId === t.id ? 'dragging' : ''}`}
                                draggable onDragStart={() => onDragStart(t.id)} onClick={() => onSelectTask(t)}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                    <PriorityDot p={t.priority} />
                                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <DueBadge d={t.dueDate} />
                                        <SubtaskBar subs={t.subtasks} />
                                    </div>
                                    <AvatarCircle a={t.assignee} size={20} />
                                </div>
                                {t.tags.length > 0 && (
                                    <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                                        {t.tags.slice(0, 2).map(tag => <span key={tag} style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)' }}>{tag}</span>)}
                                    </div>
                                )}
                                {t.governanceRequired && <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, color: '#f59e0b' }}><Shield size={9} /> Governance</div>}
                            </div>
                        ))}
                        {quickAddCol === col.key && (
                            <div style={{ padding: 8, borderRadius: 10, border: '1px solid var(--accent)', background: 'rgba(10,132,255,0.04)' }}>
                                <input value={quickAddTitle} onChange={e => onQuickAddTitle(e.target.value)} placeholder="Task title..." autoFocus
                                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: 12, outline: 'none', marginBottom: 6 }}
                                    onKeyDown={e => { if (e.key === 'Enter' && quickAddTitle.trim()) onAddTask(col.key, quickAddTitle); if (e.key === 'Escape') onQuickAddCol(null); }} />
                                <div style={{ display: 'flex', gap: 4 }}>
                                    <button onClick={() => { if (quickAddTitle.trim()) onAddTask(col.key, quickAddTitle); }} style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Add</button>
                                    <button onClick={() => onQuickAddCol(null)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-tertiary)', fontSize: 11, cursor: 'pointer' }}>Cancel</button>
                                </div>
                            </div>
                        )}
                        {colTasks.length === 0 && quickAddCol !== col.key && (
                            <div style={{ padding: '20px 10px', textAlign: 'center', fontSize: 11, color: 'var(--text-tertiary)' }}>Drop tasks here</div>
                        )}
                    </div>
                </div>
            );
        })}
    </div>
);

// ============================================================================
// TIMELINE VIEW
// ============================================================================
const TimelineView: React.FC<{ tasks: ProjectTask[]; project: Project; onSelectTask: (t: ProjectTask) => void }> = ({ tasks, project, onSelectTask }) => {
    const allDates = [...tasks.filter(t => t.dueDate).map(t => t.dueDate!), ...project.milestones.map(m => m.targetDate), project.startDate, project.targetDate].filter(Boolean);
    if (allDates.length < 2) return <div className="glass" style={{ padding: 40, textAlign: 'center' }}><Calendar size={32} style={{ color: 'var(--text-tertiary)', marginBottom: 8 }} /><p style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>Add tasks with due dates to see the timeline.</p></div>;
    const minDate = new Date(Math.min(...allDates.map(d => new Date(d).getTime())));
    const maxDate = new Date(Math.max(...allDates.map(d => new Date(d).getTime())));
    const range = Math.max(1, (maxDate.getTime() - minDate.getTime()) / 86400000);
    const toPos = (d: string) => ((new Date(d).getTime() - minDate.getTime()) / 86400000 / range) * 100;
    const todayPos = toPos(new Date().toISOString().split('T')[0]);

    return (
        <div className="glass" style={{ padding: 20, overflow: 'hidden' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>📅 Project Timeline</h3>
            {/* Milestones */}
            <div style={{ position: 'relative', height: 40, marginBottom: 8, borderBottom: '1px solid var(--divider)' }}>
                {project.milestones.map(m => (
                    <div key={m.id} style={{ position: 'absolute', left: `${toPos(m.targetDate)}%`, top: 8, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <div className={`milestone-marker ${m.status}`} />
                        <span style={{ fontSize: 9, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</span>
                    </div>
                ))}
                {todayPos >= 0 && todayPos <= 100 && <div className="timeline-today-marker" style={{ left: `${todayPos}%` }} />}
            </div>
            {/* Task bars */}
            {tasks.filter(t => t.dueDate).map(t => (
                <div key={t.id} className="timeline-row" onClick={() => onSelectTask(t)} style={{ cursor: 'pointer' }}>
                    <span style={{ width: 120, fontSize: 11, color: 'var(--text-secondary)', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>{t.title}</span>
                    <div style={{ flex: 1, position: 'relative', height: '100%' }}>
                        <div className={`timeline-bar ${t.status}`} style={{ left: `${Math.max(0, toPos(t.createdAt.split('T')[0]))}%`, width: `${Math.max(5, toPos(t.dueDate!) - toPos(t.createdAt.split('T')[0]))}%` }}>
                            {t.title}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

// ============================================================================
// INSIGHTS VIEW
// ============================================================================
const InsightsView: React.FC<{ insights: ReplicaInsight[]; tasks: ProjectTask[]; project: Project; onSelectTask: (id: string) => void }> = ({ insights, tasks, project, onSelectTask }) => {
    const grouped = { critical: insights.filter(i => i.severity === 'critical'), warning: insights.filter(i => i.severity === 'warning'), info: insights.filter(i => i.severity === 'info') };
    const ICONS: Record<string, React.ElementType> = { risk: AlertTriangle, blocker: AlertTriangle, suggestion: Lightbulb, achievement: Trophy, reminder: Bell };
    const COLORS: Record<string, string> = { risk: '#FF453A', blocker: '#FF453A', suggestion: '#0A84FF', achievement: '#10B981', reminder: '#FF9F0A' };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="glass" style={{ padding: 18 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}><Brain size={16} style={{ color: '#8b5cf6' }} /> Replica Analysis — {project.name}</h3>
                <p style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Your Replica has analyzed this project and found {insights.length} observations.</p>
            </div>
            {(['critical', 'warning', 'info'] as const).map(severity => grouped[severity].length > 0 && (
                <div key={severity}>
                    <h4 style={{ fontSize: 11, fontWeight: 700, color: severity === 'critical' ? '#FF453A' : severity === 'warning' ? '#FF9F0A' : 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                        {severity === 'critical' ? '🚨 Critical' : severity === 'warning' ? '⚠️ Warnings' : '💡 Suggestions & Info'}
                    </h4>
                    {grouped[severity].map(i => {
                        const Icon = ICONS[i.type] || Lightbulb;
                        return (
                            <div key={i.id} className={`insight-card severity-${i.severity}`} style={{ marginBottom: 10 }}>
                                <div style={{ width: 36, height: 36, borderRadius: 10, background: `${COLORS[i.type]}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Icon size={16} color={COLORS[i.type]} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <p style={{ fontSize: 13, color: 'var(--text-primary)', margin: 0, lineHeight: 1.5 }}>{i.message}</p>
                                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                        {i.actionLabel && <button style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{i.actionLabel}</button>}
                                        {i.relatedTaskId && <button onClick={() => onSelectTask(i.relatedTaskId!)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer' }}>View Task</button>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

// ============================================================================
// TASK DETAIL DRAWER
// ============================================================================
const TaskDrawer: React.FC<{ task: ProjectTask; onClose: () => void; onUpdate: (id: string, field: string, value: any) => void }> = ({ task, onClose, onUpdate }) => (
    <>
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 99 }} onClick={onClose} />
        <div className="task-drawer open" style={{ zIndex: 100, padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <PriorityDot p={task.priority} />
                    <span style={{ fontSize: 10, fontWeight: 700, color: PRIORITY_CONFIG[task.priority].color, textTransform: 'uppercase' }}>{PRIORITY_CONFIG[task.priority].label}</span>
                </div>
                <button onClick={onClose} style={{ padding: 4, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <input value={task.title} onChange={e => onUpdate(task.id, 'title', e.target.value)} style={{ width: '100%', fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', background: 'transparent', border: 'none', outline: 'none', marginBottom: 8 }} />
            <textarea value={task.description} onChange={e => onUpdate(task.id, 'description', e.target.value)} placeholder="Add a description..." rows={3} style={{ width: '100%', fontSize: 13, color: 'var(--text-secondary)', background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', outline: 'none', resize: 'vertical', marginBottom: 16 }} />
            {/* Meta */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                <div style={{ padding: 10, borderRadius: 10, background: 'var(--bg-tertiary)' }}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Status</label>
                    <select value={task.status} onChange={e => onUpdate(task.id, 'status', e.target.value)} style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, outline: 'none', cursor: 'pointer' }}>
                        {KANBAN_COLUMNS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                </div>
                <div style={{ padding: 10, borderRadius: 10, background: 'var(--bg-tertiary)' }}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Priority</label>
                    <select value={task.priority} onChange={e => onUpdate(task.id, 'priority', e.target.value)} style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, outline: 'none', cursor: 'pointer' }}>
                        {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                </div>
            </div>
            {task.dueDate && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>📅 Due: {fmtDate(task.dueDate)} <DueBadge d={task.dueDate} /></div>}
            {task.assignee && <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}><AvatarCircle a={task.assignee} size={20} /> {task.assignee.name}</div>}
            {task.governanceRequired && <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', fontSize: 11, color: '#f59e0b', fontWeight: 600, marginBottom: 12 }}><Shield size={12} /> Requires Decision Guardian™ approval</div>}
            {/* Subtasks */}
            {task.subtasks.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <h4 style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: 8 }}>Subtasks</h4>
                    {task.subtasks.map((sub, i) => (
                        <button key={sub.id} onClick={() => { const updated = [...task.subtasks]; updated[i] = { ...sub, completed: !sub.completed }; onUpdate(task.id, 'subtasks', updated); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '6px 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                            {sub.completed ? <CheckCircle2 size={16} color="#10b981" /> : <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid var(--border)' }} />}
                            <span style={{ fontSize: 13, color: sub.completed ? 'var(--text-tertiary)' : 'var(--text-primary)', textDecoration: sub.completed ? 'line-through' : 'none' }}>{sub.title}</span>
                        </button>
                    ))}
                </div>
            )}
            {/* Tags */}
            {task.tags.length > 0 && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {task.tags.map(tag => <span key={tag} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}><Tag size={9} style={{ verticalAlign: -1, marginRight: 3 }} />{tag}</span>)}
                </div>
            )}
        </div>
    </>
);
